import { Box, Typography, Button, IconButton, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import useResultService from "../../api/services/ResultsService";
import LoadingSpinner from "../../components/LoadingSpinner";
import ExportPDFButton from "../../components/ExportPDFButton";

// ---------- GRADE FROM CONVERTED MARK ----------
const getGrade = (marks) => {
  if (marks >= 75) return "A";
  if (marks >= 65) return "B";
  if (marks >= 45) return "C";
  if (marks >= 30) return "D";
  return "F";
};

// ---------- DIVISION LOGIC ----------
const getGradePoint = (grade) => {
  switch (grade) {
    case "A":
      return 1;
    case "B":
      return 2;
    case "C":
      return 3;
    case "D":
      return 4;
    case "F":
      return 5;
  }
};

const getDivisionLabel = (points) => {
  if (points >= 1 && points <= 17) return `Division 1 . ${points}`;
  if (points >= 18 && points <= 21) return `Division 2 . ${points}`;
  if (points >= 22 && points <= 25) return `Division 3 . ${points}`;
  if (points >= 26 && points <= 33) return `Division 4 . ${points}`;
  return `Division 0`;
};

const determineDivision = (subjects) => {
  const pointsArray = subjects.map((sub) => getGradePoint(sub.grade));

  if (pointsArray.length < 7) return null;

  if (pointsArray.length === 7) {
    return pointsArray.reduce((sum, point) => sum + point, 0);
  }

  const bestSeven = [...pointsArray].sort((a, b) => a - b).slice(0, 7);
  return bestSeven.reduce((sum, point) => sum + point, 0);
};

const ClassResults = () => {
  const location = useLocation();
  const classId = location.state?.classId || location.state?.id;
  const examinationTypeId = location.state?.examId;

  const theme = useTheme();
  const navigate = useNavigate();

  const { fetchClassResults, sendSmsToParents } = useResultService();

  const fetchClassResultsRef = useRef(fetchClassResults);
  const sendSmsToParentsRef = useRef(sendSmsToParents);

  useEffect(() => {
    fetchClassResultsRef.current = fetchClassResults;
  }, [fetchClassResults]);

  useEffect(() => {
    sendSmsToParentsRef.current = sendSmsToParents;
  }, [sendSmsToParents]);

  const [allResults, setAllResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [classMeta, setClassMeta] = useState({
    className: "N/A",
    classTitle: "N/A",
    examinationType: "N/A",
  });

  const [statusView, setStatusView] = useState("ACTIVE");

  // default view is 40
  const [marksView, setMarksView] = useState("40");

  const [sendingStatus, setSendingStatus] = useState({});
  const [sentStatus, setSentStatus] = useState({});

  useEffect(() => {
    const savedStatus = localStorage.getItem(
      `sentStatus_${classId}_${examinationTypeId}`
    );
    if (savedStatus) {
      setSentStatus(JSON.parse(savedStatus));
    }
  }, [classId, examinationTypeId]);

  // Convert subject mark from /40 to percentage
  const convertToPercentage = (value) => {
    const numeric = parseFloat(value);
    if (isNaN(numeric)) return 0;
    return parseFloat(((numeric / 40) * 100).toFixed(1));
  };

  const formatNumber = (num) => {
    if (num == null || isNaN(num)) return "";
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
  };

  /** SMS always uses marks on the 100 (%) scale — same as convertedMarks / 100% view. */
  const getMarksOutOf100 = (details) => {
    if (!details) return 0;
    if (
      details.convertedMarks !== undefined &&
      details.convertedMarks !== null
    ) {
      const p = parseFloat(details.convertedMarks);
      return Number.isFinite(p) ? p : 0;
    }
    if (details.rawMarks !== undefined && details.rawMarks !== null) {
      return convertToPercentage(details.rawMarks);
    }
    return 0;
  };

  const handleSendSMS = async (row) => {
    const studentId = row.id;
    setSendingStatus((prev) => ({ ...prev, [studentId]: true }));

    try {
      const subjectsSummary = Object.entries(row.subjects || {}).reduce(
        (acc, [subject, details]) => {
          acc[subject] = {
            marks: getMarksOutOf100(details),
            grade: details?.grade || "",
          };
          return acc;
        },
        {}
      );

      const total100 =
        row.totalMarks100 != null && !isNaN(Number(row.totalMarks100))
          ? parseFloat(Number(row.totalMarks100).toFixed(1))
          : 0;
      const avg100 =
        row.average100 != null && !isNaN(row.average100)
          ? parseFloat(row.average100.toFixed(1))
          : 0;

      const payload = {
        studentName: row.studentName,
        studentNumber: row.studentNumber,
        subjects: subjectsSummary,
        totalMarks: total100,
        average: avg100,
        position: row.position ? parseInt(row.position) : 0,
        examinationType: row.examinationType || "",
        classTitle: row.classTitle || "",
        division: row.originalDivision || "N/A",
      };

      await sendSmsToParentsRef.current(studentId, payload);

      setSentStatus((prev) => {
        const newStatus = { ...prev, [studentId]: true };
        localStorage.setItem(
          `sentStatus_${classId}_${examinationTypeId}`,
          JSON.stringify(newStatus)
        );
        return newStatus;
      });
    } catch (error) {
      console.error("Error sending SMS", error);
    } finally {
      setSendingStatus((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadResults = async () => {
      if (!classId || !examinationTypeId) return;

      setIsLoading(true);
      try {
        const data = await fetchClassResultsRef.current(
          classId,
          examinationTypeId
        );

        if (!isMounted) return;

        if (data && Array.isArray(data.data)) {
          const first = data.data[0];
          setClassMeta({
            className: first?.className || "N/A",
            classTitle: first?.classTitle || "N/A",
            examinationType: first?.examinationType || "N/A",
          });

          const studentMap = {};

          data.data.forEach((item) => {
            if (!studentMap[item.studentId]) {
              studentMap[item.studentId] = {
                id: item.studentId,
                studentName: item.studentName,
                studentNumber: item.studentNumber,
                combination: item.combination || "N/A",
                className: item.className,
                classTitle: item.classTitle,
                examinationType: item.examinationType,
                studentStatus: item.studentStatus || "",
                subjects: {},
              };
            }

            const rawMarks = parseFloat(item.marks) || 0;
            const convertedMarks = convertToPercentage(rawMarks);
            const grade = getGrade(convertedMarks);

            studentMap[item.studentId].subjects[item.subjectName] = {
              rawMarks,
              convertedMarks,
              grade,
            };
          });

          const processed = Object.values(studentMap).map((student) => {
            const subjectValues = Object.values(student.subjects || {});
            const subjectCount = subjectValues.length;

            const rawSubjectMarks = subjectValues.map((s) => s.rawMarks || 0);
            const convertedSubjectMarks = subjectValues.map(
              (s) => s.convertedMarks || 0
            );

            const totalMarks40 = parseFloat(
              rawSubjectMarks.reduce((sum, mark) => sum + mark, 0).toFixed(1)
            );
            const average40 = rawSubjectMarks.length
              ? parseFloat((totalMarks40 / rawSubjectMarks.length).toFixed(1))
              : 0;

            const totalMarks100 = parseFloat(
              convertedSubjectMarks
                .reduce((sum, mark) => sum + mark, 0)
                .toFixed(1)
            );
            const average100 = convertedSubjectMarks.length
              ? parseFloat(
                  (totalMarks100 / convertedSubjectMarks.length).toFixed(1)
                )
              : 0;

            const isComplete = subjectCount >= 7;
            const divisionPoints = determineDivision(subjectValues);
            const originalDivision = isComplete
              ? getDivisionLabel(divisionPoints)
              : "Incomplete";

            return {
              ...student,
              subjectCount,
              isComplete,
              totalMarks40,
              average40,
              totalMarks100,
              average100,
              originalDivision,
            };
          });

          setAllResults(processed);
        } else {
          setAllResults([]);
          setClassMeta({
            className: "N/A",
            classTitle: "N/A",
            examinationType: "N/A",
          });
        }
      } catch (e) {
        console.error("Error loading results:", e);
        if (isMounted) {
          setAllResults([]);
          setClassMeta({
            className: "N/A",
            classTitle: "N/A",
            examinationType: "N/A",
          });
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadResults();

    return () => {
      isMounted = false;
    };
  }, [classId, examinationTypeId]);

  const results = useMemo(() => {
    const isActive = (s) =>
      String(s || "")
        .trim()
        .toUpperCase() === "ACTIVE";

    const base =
      statusView === "ALL"
        ? allResults
        : allResults.filter((s) => isActive(s.studentStatus));

    const getAverage = (student) =>
      marksView === "40" ? student.average40 : student.average100;

    const getTotal = (student) =>
      marksView === "40" ? student.totalMarks40 : student.totalMarks100;

    const completeStudents = base
      .filter((s) => s.isComplete)
      .sort((a, b) => {
        if (getAverage(b) === getAverage(a)) {
          return getTotal(b) - getTotal(a);
        }
        return getAverage(b) - getAverage(a);
      })
      .map((student, index) => ({
        ...student,
        totalMarks: getTotal(student),
        average: getAverage(student),
        division: marksView === "40" ? "-" : student.originalDivision,
        position: index + 1,
      }));

    const incompleteStudents = base
      .filter((s) => !s.isComplete)
      .sort((a, b) => a.studentName.localeCompare(b.studentName))
      .map((student) => ({
        ...student,
        totalMarks: getTotal(student),
        average: getAverage(student),
        division: marksView === "40" ? "-" : "Incomplete",
        position: "",
      }));

    return [...completeStudents, ...incompleteStudents];
  }, [allResults, statusView, marksView]);

  const createColumns = (rows) => {
    if (!rows.length) return [];

    const subjectSet = new Set();
    rows.forEach((student) =>
      Object.keys(student.subjects || {}).forEach((s) => subjectSet.add(s))
    );
    const subjectNames = Array.from(subjectSet);

    const columns = [
      {
        field: "studentName",
        headerName: "Student Name",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "studentNumber",
        headerName: "Index Number",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "combination",
        headerName: "Combination",
        flex: 0.8,
        minWidth: 120,
      },
    ];

    subjectNames.forEach((subject) => {
      columns.push({
        field: `subject_${subject}`,
        headerName: subject,
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => {
          const subjectData = row.subjects?.[subject];
          if (!subjectData) return <Typography>-</Typography>;

          const displayedMarks =
            marksView === "40"
              ? subjectData.rawMarks
              : subjectData.convertedMarks;

          return (
            <Box>
              <Typography>{formatNumber(displayedMarks)}</Typography>
              <Typography variant="body2">{subjectData.grade}</Typography>
            </Box>
          );
        },
      });
    });

    columns.push(
      {
        field: "totalMarks",
        headerName: "Total",
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">
            {formatNumber(row.totalMarks)}
          </Typography>
        ),
      },
      {
        field: "average",
        headerName: "Average",
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">
            {formatNumber(row.average)}
          </Typography>
        ),
      },
      {
        field: "division",
        headerName: "Division",
        flex: 0.9,
        minWidth: 140,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.division || ""}</Typography>
        ),
      },
      {
        field: "position",
        headerName: "Position",
        flex: 0.5,
        minWidth: 90,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.position || ""}</Typography>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton
              color="secondary"
              onClick={() =>
                navigate(
                  `/student_results/${row.id}/${classId}/${examinationTypeId}`
                )
              }
            >
              <VisibilityOutlinedIcon />
            </IconButton>

            <Button
              variant="contained"
              color="secondary"
              size="small"
              disabled={sendingStatus[row.id]}
              onClick={() => handleSendSMS(row)}
              sx={{
                ...(sentStatus[row.id] && {
                  backgroundColor: "#0A7A2A",
                  color: "white",
                  "&:hover": { backgroundColor: "#06661F" },
                }),
              }}
            >
              {sendingStatus[row.id]
                ? "Sending..."
                : sentStatus[row.id]
                ? "Sent"
                : "Send SMS"}
            </Button>
          </Box>
        ),
      }
    );

    return columns;
  };

  return (
    <Box m="20px">
      <Box p={2}>
        <Header
          title={`CLASS RESULTS FOR: ${classMeta.className} | CLASS TITLE: ${classMeta.classTitle} | EXAM: ${classMeta.examinationType}`}
        />

        <Box
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant={statusView === "ACTIVE" ? "contained" : "outlined"}
              color="secondary"
              onClick={() => setStatusView("ACTIVE")}
              sx={{
                ...(statusView === "ACTIVE" && {
                  backgroundColor: theme.palette.secondary.main,
                  "&:hover": { backgroundColor: theme.palette.secondary.dark },
                }),
              }}
            >
              ACTIVE
            </Button>

            <Button
              variant={statusView === "ALL" ? "contained" : "outlined"}
              color="secondary"
              onClick={() => setStatusView("ALL")}
              sx={{
                ...(statusView === "ALL" && {
                  backgroundColor: theme.palette.secondary.main,
                  "&:hover": { backgroundColor: theme.palette.secondary.dark },
                }),
              }}
            >
              ALL
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={() =>
                setMarksView((prev) => (prev === "40" ? "100" : "40"))
              }
            >
              {marksView === "40" ? "100" : "40"}
            </Button>
          </Box>

          <ExportPDFButton
            groupedResults={results}
            isClassResults={true}
            className={classMeta.className}
            classTitle={classMeta.classTitle}
            examinationType={classMeta.examinationType}
            marksView={marksView}
          />
        </Box>

        {isLoading ? (
          <LoadingSpinner />
        ) : results.length > 0 ? (
          <Box
            sx={{
              height: "75vh",
              "& .MuiDataGrid-root": { border: "none" },
              "& .MuiDataGrid-cell": {
                whiteSpace: "normal",
                wordWrap: "break-word",
                lineHeight: "1.4rem",
              },
            }}
          >
            <DataGrid
              rows={results}
              columns={createColumns(results)}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              getRowHeight={() => 80}
            />
          </Box>
        ) : (
          <Typography
            variant="h6"
            color="textSecondary"
            textAlign="center"
            mt={4}
          >
            {statusView === "ACTIVE"
              ? "No ACTIVE students results found"
              : "No results found"}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ClassResults;